export type ExperienceLocale = 'en' | 'da';

export interface ExperienceEntry {
  id: string;
  date: string;
  title: string;
  company: string;
  companyUrl: string;
  location: string;
  description: string;
  skills: string[];
  /** Danish date range (e.g. "2023 — Nuværende"). */
  dateDa?: string;
  /** Danish job title. */
  titleDa?: string;
  /** Danish location line. */
  locationDa?: string;
  /** Danish description. */
  descriptionDa?: string;
}

function getLocalized<T>(entry: ExperienceEntry, locale: ExperienceLocale, en: T, da: T | undefined): T {
  return (locale === 'da' && da != null) ? da : en;
}

export function getEntryDate(entry: ExperienceEntry, locale: ExperienceLocale): string {
  return getLocalized(entry, locale, entry.date, entry.dateDa);
}

export function getEntryTitle(entry: ExperienceEntry, locale: ExperienceLocale): string {
  return getLocalized(entry, locale, entry.title, entry.titleDa);
}

export function getEntryLocation(entry: ExperienceEntry, locale: ExperienceLocale): string {
  return getLocalized(entry, locale, entry.location, entry.locationDa);
}

export function getEntryDescription(entry: ExperienceEntry, locale: ExperienceLocale): string {
  return getLocalized(entry, locale, entry.description, entry.descriptionDa);
}

/** Experience entries, most recent first. */
export const experienceEntries: ExperienceEntry[] = [
  {
    id: 'seatgeek-lead',
    date: '2023 — Present',
    dateDa: '2023 — Nuværende',
    title: 'Lead Software Engineer',
    titleDa: 'Lead Software Engineer',
    company: 'SeatGeek',
    companyUrl: 'https://seatgeek.com',
    location: '🇩🇰 Copenhagen, Denmark · Remote',
    locationDa: '🇩🇰 København, Danmark · Fjernarbejde',
    description:
      'Leads platform, developer experience, and AI initiatives with a focus on reliability and long-term maintainability. Drives internal AI systems toward production readiness by defining expectations around evaluation, observability, and operational quality in non-deterministic workflows. Shapes how engineers adopt new tools by applying a product-first mindset that prioritizes close collaboration, user research, and usage metrics.',
    descriptionDa:
      'Leder platform-, developer experience- og AI-initiativer med fokus på pålidelighed og langsigtet vedligeholdelse. Driver interne AI-systemer mod produktionsklarhed ved at definere forventninger om evaluering, observabilitet og operationel kvalitet i ikke-deterministiske workflows. Formår hvordan ingeniører adopterer nye værktøjer ved at anvende et produkt-først mindset med tæt samarbejde, brugerresearch og anvendelsesmålinger.',
    skills: [
      'Agentic Systems',
      'MCP Servers',
      'Open Source',
      'Kubernetes',
      'AWS',
      'Internal Developer Platforms',
      'Terraform',
      'ArgoCD',
      'CI/CD',
      'Python',
      'Golang',
      'Datadog',
    ],
  },
  {
    id: 'seatgeek-senior',
    date: '2020 — 2023',
    dateDa: '2020 — 2023',
    title: 'Senior Software Engineer',
    titleDa: 'Senior Software Engineer',
    company: 'SeatGeek',
    companyUrl: 'https://seatgeek.com',
    location: '🇩🇰 Copenhagen, Denmark · Remote',
    locationDa: '🇩🇰 København, Danmark · Fjernarbejde',
    description:
      'Builds and operates backend systems that power ticketing, integrations, and high-traffic customer workflows. Leads zero-downtime migrations and performance improvements for services where availability and correctness directly affect revenue and customer trust.',
    descriptionDa:
      'Bygger og driver backend-systemer der understøtter billetløsninger, integrationer og kundeworkflows under høj belastning. Leder nedetidsfri migrationer og performance-forbedringer for tjenester hvor tilgængelighed og korrekthed direkte påvirker omsætning og kundetillid.',
    skills: [
      'Ticketing Integrations',
      'C#',
      '.NET Core',
      'Python',
      'PostgreSQL',
      'RabbitMQ',
      'Redis',
      'AWS',
      'Datadog',
      'Incident Response',
    ],
  },
  {
    id: 'seatgeek-platform',
    date: '2019 — 2020',
    dateDa: '2019 — 2020',
    title: 'Platform Engineer',
    titleDa: 'Platform Engineer',
    company: 'SeatGeek',
    companyUrl: 'https://seatgeek.com',
    location: '🇩🇰 Copenhagen, Denmark · Hybrid',
    locationDa: '🇩🇰 København, Danmark · Hybrid',
    description:
      'Designs and builds internal release and deployment tooling used by engineers across product teams. Improves observability and deployment safety by standardizing how services are onboarded, configured, and monitored across environments. Partners directly with product engineers to adjust build, deploy, and release workflows.',
    descriptionDa:
      'Designer og bygger interne release- og deployment-værktøjer til ingeniører på tværs af produktteams. Forbedrer observabilitet og deployment-sikkerhed ved at standardisere hvordan tjenester onboardes, konfigureres og overvåges på tværs af miljøer. Samarbejder direkte med produktingeniører om build-, deploy- og release-workflows.',
    skills: [
      'Release Engineering',
      'Go',
      'TypeScript',
      'React',
      'Terraform',
      'AWS',
      'Nomad',
      'Vault',
      'PostgreSQL',
      'Hasura',
      'GraphQL',
      'Datadog',
    ],
  },
  {
    id: 'heb-devops',
    date: '2017 — 2019',
    dateDa: '2017 — 2019',
    title: 'DevOps Engineer',
    titleDa: 'DevOps Engineer',
    company: 'H·E·B',
    companyUrl: 'https://www.heb.com',
    location: '🇺🇸 Texas, United States · On-site',
    locationDa: '🇺🇸 Texas, USA · På kontoret',
    description:
      'Builds and maintains CI/CD pipelines used by multiple application teams shipping to production on Kubernetes. Partners directly with application teams to migrate services from on-premise to containerized environments and resolve bottlenecks in development workflows.',
    descriptionDa:
      'Bygger og vedligeholder CI/CD-pipeliner til flere applikationsteams der udruller til produktion på Kubernetes. Samarbejder direkte med applikationsteams om at migrere tjenester fra on-premise til containermiljøer og løse flaskehalse i udviklingsworkflows.',
    skills: [
      'Python',
      'Helm',
      'CI/CD',
      'Kubernetes',
      'Docker',
      'Google Cloud Platform',
      'Terraform',
      'CircleCI',
      'Datadog',
    ],
  },
];
